import * as React from 'react'

import { getCookie } from '../../../app/shared/cookie'
import { setCookieRequest } from '../../services/api'

/*
 * Context.
 */
export type FinancialsTableSettingsType = Record<string, unknown>

export type FinancialsTableScrollInfo = {
  yPosition: number
}

export interface FinancialsTableContextProps<T extends FinancialsTableSettingsType> {
  settings: T
  setSettings: (settings: T) => void
  scrollInfo?: FinancialsTableScrollInfo
}

export const FinancialsTableContext = React.createContext<FinancialsTableContextProps<FinancialsTableSettingsType>>({
  settings: {},
  setSettings: () => null,
  scrollInfo: undefined,
})

/*
 * Hook.
 */

export function useFinancialsTableContext<T extends FinancialsTableSettingsType = FinancialsTableSettingsType>() {
  return React.useContext(FinancialsTableContext) as FinancialsTableContextProps<T>
}

/*
 * Component.
 */

interface Props {
  children: React.ReactNode
  defaultSettings: FinancialsTableSettingsType
  cookieKey: string
}

export const FinancialsTableContextProvider = ({ children, cookieKey, defaultSettings }: Props) => {
  const [scrollInfo, setScrollInfo] = React.useState<FinancialsTableScrollInfo>()
  const [settings, setSettings] = React.useState(() => {
    const cookieValue = decodeURIComponent(getCookie(cookieKey))
    if (cookieValue.length === 0) return defaultSettings

    try {
      return { ...defaultSettings, ...JSON.parse(cookieValue) } as FinancialsTableSettingsType
    } catch (_) {
      return defaultSettings
    }
  })

  const handleSetSettingsChange = (settings: FinancialsTableSettingsType) => {
    setScrollInfo({ yPosition: window.scrollY })
    setSettings(settings)
    setCookieRequest(cookieKey, JSON.stringify(settings))
  }

  return (
    <FinancialsTableContext.Provider value={{ settings, setSettings: handleSetSettingsChange, scrollInfo }}>
      {children}
    </FinancialsTableContext.Provider>
  )
}
